import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

export type BadgeTone = 'neutral' | 'gold' | 'red' | 'success' | 'warning' | 'danger' | 'info';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Renders a leading status dot in the tone colour. */
  dot?: boolean;
}

const tones: Record<BadgeTone, string> = {
  neutral: 'bg-surface-subtle text-ink-muted ring-border',
  gold: 'bg-gold-100 text-gold-800 ring-gold-200',
  red: 'bg-red-100 text-red-700 ring-red-200',
  success: 'bg-state-success-bg text-state-success ring-gold-200',
  warning: 'bg-state-warning-bg text-state-warning ring-gold-200',
  danger: 'bg-state-danger-bg text-state-danger ring-red-200',
  info: 'bg-state-info-bg text-state-info ring-gray-300',
};

const dotTones: Record<BadgeTone, string> = {
  neutral: 'bg-ink-subtle',
  gold: 'bg-gold-500',
  red: 'bg-red-500',
  success: 'bg-gold-600',
  warning: 'bg-gold-600',
  danger: 'bg-red-500',
  info: 'bg-gray-500',
};

export function Badge({ tone = 'neutral', dot = false, className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
        tones[tone],
        className,
      )}
      {...rest}
    >
      {dot && <span className={cn('h-1.5 w-1.5 rounded-full', dotTones[tone])} />}
      {children}
    </span>
  );
}
